import { useState } from "react";
import { Text, View } from "react-native";
import { ScreenContainer, ScrollableContainer } from "../../components";
import { IconButton } from "../../components/styled";
import { useQuery } from "@apollo/client";
import { PDR_HISTORY_DETAILS_QUERY } from "./queries";
import { PdrHistory } from "../../screens/fragments";
import { ScreenProps } from "../types";
import { ActivityIndicator } from "react-native";
import { formatToUtc } from "../../utils/dateUtils";
import Feather from "@expo/vector-icons/Feather";
import { colors } from "../../../theme";
import PopOver from "../../components/PopOver";
import Button from "../../components/Button";
import { openBrowser } from "../../screens/utils";
import { PDR_LINK } from "../../screens/links";

type PdrHistoryDetailsQuery = {
  pdrHistoryDetails: PdrHistory;
};
type ModalProps = {
  onDismiss(): void;
  visible: boolean;
};

export const Modal = ({ onDismiss, visible }: ModalProps) => {
  return (
    <PopOver visible={visible} onDismiss={onDismiss} size="md">
      <Text className="mb-6 text-title-5 font-700 text-ink-dark">Unable to apply requested credits to PDR requirements.</Text>
      <Text className="text-regular-normal font-400 mb-2">
        The credits you've requested do not meet the qualifications to be added to your PDR requirements.{" "}
        <Text className="text-primary text-regular-normal font-400 underline" onPress={openBrowser(PDR_LINK)}>
          Learn more
        </Text>{" "}
        about qualifying PDR activities and submission guidelines.
      </Text>
      <View className="flex-row justify-end mt-4">
        <Button onPress={openBrowser(PDR_LINK)} size="small">
          Learn More
        </Button>
        <Button onPress={onDismiss} size="small" mode="secondary" className="ml-2">
          Okay
        </Button>
      </View>
    </PopOver>
  );
};

export default function PdrDetailsScreen({ route }: ScreenProps<"PdrDetails">) {
  const { id } = route.params;
  const [openModal, setOpenModal] = useState(false);
  const { data, loading } = useQuery<PdrHistoryDetailsQuery>(PDR_HISTORY_DETAILS_QUERY, {
    variables: {
      id,
    },
  });
  const { pdrHistoryDetails } = data || {};

  const participationDates = pdrHistoryDetails?.participationDates;

  const isMultipleDates = participationDates?.endedOn && participationDates.startedOn !== participationDates.endedOn;
  const creditsRequested = pdrHistoryDetails?.creditsAppliedFor || 0;
  const creditsAppliedToRequirement = pdrHistoryDetails?.creditsAccepted || 0;

  const details = [
    [
      {
        title: "PDR Activity",
        value: pdrHistoryDetails?.category || "N/A",
      },
    ],
    [
      {
        title: "Requested",
        value: `${creditsRequested} Credits`,
      },
      {
        title: "Applied to Requirement",
        value: `${creditsAppliedToRequirement} Credits`,
        icon:
          creditsAppliedToRequirement < creditsRequested ? (
            <Feather name="help-circle" size={18} color={colors.ink.darkest} onPress={() => setOpenModal(true)} />
          ) : null,
      },
    ],
    [
      {
        title: "Credit Type",
        value: pdrHistoryDetails?.creditType || "N/A",
      },
    ],
    [
      {
        title: `Participation Date${isMultipleDates ? "(s)" : ""}`,
        value: `${formatToUtc(participationDates?.startedOn || "")}${
          isMultipleDates ? ` - ${formatToUtc(pdrHistoryDetails?.participationDates?.endedOn || "")}` : ""
        }`,
      },
    ],
    ...(pdrHistoryDetails?.location
      ? [
          [
            {
              title: "Location",
              value: pdrHistoryDetails?.location,
            },
          ],
        ]
      : []),
    [
      {
        title: "Description",
        value: `${pdrHistoryDetails?.description1 || "N/A"}`,
      },
    ],
    [
      {
        title: "Additional Information",
        value: `${pdrHistoryDetails?.description2 || "N/A"}`,
      },
    ],
  ];

  if (loading) {
    return <ActivityIndicator className="py-32 items-center justify-center mt-6" />;
  }

  return (
    <ScreenContainer testID="pdrDetailsScreen" navigationTitle="PDR Details">
      <ScrollableContainer>
        <View className="flex-row justify-between items-center px-6 py-6 border-t border-b border-sky-light bg-sky-lightest web:mt-3 md:px-4 md:border-l md:border-r md:rounded-lg md:mt-10">
          <View>
            <Text className="text-regular-tight font-700 text-ink-darkest">Reviewed</Text>
            <Text className="text-small-normal font-400 text-ink mt-2">
              Submitted {formatToUtc(pdrHistoryDetails?.submittedOn || "")}
            </Text>
          </View>
          <IconButton icon="check" size={24} style={{ margin: 0 }} />
        </View>
        <View>
          {details.map((detail, index) => {
            const isLast = index === details.length - 1;
            return (
              <View
                className={`flex-row justify-between items-center px-6 py-6 border-b border-sky-light md:px-4 ${
                  isLast ? "border-b-0" : ""
                }`}
                key={index}
              >
                {detail.map((item, index) => (
                  <View className="flex-1" key={index}>
                    <Text className="text-small-tight font-700 text-ink-darkest">{item.title}</Text>
                    <View className="flex-row items-center gap-1 mt-4">
                      <Text className="text-regular-tight font-400 text-ink">{item.value || "--"}</Text>
                      {item.icon}
                    </View>
                  </View>
                ))}
              </View>
            );
          })}
        </View>
        <Modal onDismiss={() => setOpenModal(false)} visible={openModal} />
      </ScrollableContainer>
    </ScreenContainer>
  );
}
